import React, { useState } from "react";
import Hero from "./Hero";
import Features from "./Features";
import Pricing from "./Pricing";
import Testimonials from "./Testimonials";
import FAQ from "./FAQ";
import CTASection from "./CTASection";
import Footer from "./Footer";
import Navbar from "./Navbar";
import DemoExamSection from "./DemoExamSection";
import HowItWorks from "./HowItWorks";
import Comparison from "./Comparison";
import TrustBar from "./TrustBar";
import StickyCTA from "./StickyCTA";
import LoginModal from "../Auth/LoginModal";
import RegisterModal from "../Auth/RegisterModal";
import DemoModal from "./DemoModal";
import ForgotPasswordModal from "../Auth/ForgotPasswordModal";
// Primitives premium importées UNIQUEMENT par l'arbre Landing → tree-shakées
// hors du bundle authentifié (DESIGN-SPEC §2 / §7). Reveal orchestre la cascade
// éditoriale on-load ; useInView pilote un voile mesh révélé une seule fois.
import Reveal from "../ui/Reveal";
import useInView from "../../hooks/useInView";
import { siteConfig } from "../../config/siteConfig";

const LandingPage = ({ onLoginClick, onRegisterClick } = {}) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Texte du lien d'évitement (a11y) — clé config optionnelle, fallback sûr.
  const skipLabel =
    (siteConfig.uiCopy && siteConfig.uiCopy.skipToContent) ||
    "Siirry sisältöön";

  // Voile mesh révélé une seule fois quand le contenu entre en vue : effet
  // purement décoratif (opacity), aria-hidden, neutralisé sous reduced-motion
  // par le hook partagé (inView=true d'emblée) → 0 CLS, 0 mouvement requis.
  const { ref: meshRef, inView: meshInView } = useInView({ threshold: 0 });

  // Ouverture du login : on respecte le câblage App.jsx (onLoginClick) quand il
  // est fourni, sinon on retombe sur l'état interne (la landing reste autonome).
  const handleLogin = () => {
    if (typeof onLoginClick === "function") onLoginClick();
    else setShowLogin(true);
  };

  // Ouverture du registre : même logique, câblage App.jsx prioritaire.
  const handleRegister = () => {
    if (typeof onRegisterClick === "function") onRegisterClick();
    else {
      setShowLogin(false);
      setShowRegister(true);
    }
  };

  // Función específica para la transición Demo -> Registro
  const handleRegisterFromDemo = () => {
    setIsDemoOpen(false); // Cierra la demo
    handleRegister(); // Abre el registro (câblage App.jsx ou état interne)
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-slate-50">
      {/* Lien d'évitement : premier élément focusable, vise le <main> unique. */}
      <a
        href="#landing-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[200] focus:rounded-lg focus:bg-teal-700 focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white focus:shadow-lg"
      >
        {skipLabel}
      </a>

      {/* Toile de fond premium : canvas slate-50 + mesh statique + grain.
          Décoratif, derrière le contenu, sans impact géométrique (0 CLS). */}
      <div
        ref={meshRef}
        aria-hidden="true"
        className={`grain pointer-events-none absolute inset-0 z-0 bg-mesh transition-opacity duration-700 ${
          meshInView ? "opacity-100" : "opacity-0"
        }`}
      />

      <div className="relative z-10">
        <Navbar
          onLoginClick={handleLogin}
          onRegisterClick={() => {
            setShowLogin(false);
            handleRegister();
          }}
        />

        {/* <main> UNIQUE de la landing (cible du skip-link). */}
        <main id="landing-main">
          {/* Cascade éditoriale on-load : eyebrow→preuve, staggerée par index. */}
          <Reveal index={0}>
            {/* Hero Section */}
            <Hero
              onCtaClick={() => handleRegister()}
              onDemoClick={() => setIsDemoOpen(true)}
            />
          </Reveal>

          {/* Banda de confianza (hechos reales, no testimonios) */}
          <Reveal index={1}>
            <TrustBar />
          </Reveal>

          <Reveal index={2}>
            <Features />
          </Reveal>

          {/* Näin se toimii en 3 pasos */}
          <HowItWorks />

          {/* Sección Demo (probar antes de pagar) */}
          <DemoExamSection onDemoClick={() => setIsDemoOpen(true)} />

          {/* Mitä sisältyy la plataforma */}
          <Testimonials />

          {/* Comparativa con/sin la plataforma */}
          <Comparison />

          <Pricing onRegisterClick={() => handleRegister()} />

          <FAQ />

          {/* CTA Final */}
          <CTASection onCtaClick={() => handleRegister()} />
        </main>

        <Footer />

        {/* Espaciador para que la barra CTA fija no tape el footer en móvil */}
        <div className="h-20 lg:hidden" aria-hidden="true" />

        {/* Barra CTA fija en móvil */}
        <StickyCTA onCtaClick={() => handleRegister()} />
      </div>

      {/* --- MODALES --- */}

      {/* Login Modal */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
        onForgotPassword={() => {
          setShowLogin(false);
          setShowForgot(true);
        }}
      />
      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSwitchToLogin={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />

      {/* Demo Modal: Ahora con onRegister conectado */}
      <DemoModal
        isOpen={isDemoOpen}
        onClose={() => setIsDemoOpen(false)}
        onRegister={handleRegisterFromDemo}
      />

      <ForgotPasswordModal
        isOpen={showForgot}
        onClose={() => setShowForgot(false)}
      />
    </div>
  );
};

export default LandingPage;
